'use client';
import React from 'react';
import { Sparkles, ArrowRight } from 'lucide-react';
import Link from 'next/link';

export default function FounderSection() {
  return (
    <section id="founder" className="py-24 relative overflow-hidden" style={{ background: '#0B0F19' }}>
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 50% 40% at 30% 50%, rgba(0,212,255,0.04) 0%, transparent 70%)' }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Founder Photo Placeholder */}
          <div className="flex justify-center lg:justify-start">
            <div className="relative">
              <div
                className="w-72 h-72 lg:w-80 lg:h-80 rounded-3xl flex items-center justify-center relative overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, rgba(0,212,255,0.10) 0%, rgba(167,139,250,0.08) 100%)',
                  border: '1px solid rgba(0,212,255,0.20)',
                  boxShadow: '0 8px 40px rgba(0,212,255,0.10)',
                }}
              >
                <div
                  className="absolute inset-0"
                  style={{ background: 'radial-gradient(circle at 50% 30%, rgba(0,212,255,0.12) 0%, transparent 60%)' }}
                />
                {/* Founder initials placeholder */}
                <div className="relative text-center">
                  <div
                    className="w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center text-3xl font-extrabold"
                    style={{
                      background: 'linear-gradient(135deg, #00D4FF, #0099CC)',
                      color: '#0B0F19',
                    }}
                  >
                    F
                  </div>
                  <p className="text-sm font-semibold" style={{ color: 'rgba(240,244,255,0.50)' }}>Founder Photo</p>
                  <p className="text-xs mt-1" style={{ color: 'rgba(240,244,255,0.30)' }}>Coming soon</p>
                </div>
              </div>
              {/* Floating badge */}
              <div
                className="absolute -bottom-4 -right-4 rounded-2xl px-4 py-3"
                style={{
                  background: 'rgba(11,15,25,0.95)',
                  border: '1px solid rgba(0,212,255,0.20)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.30)',
                }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-xs font-semibold" style={{ color: 'rgba(240,244,255,0.70)' }}>Building in public</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Founder Story */}
          <div>
            <div className="section-badge mb-6">
              <Sparkles size={12} />
              Why We Built This
            </div>
            <h2
              className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-6"
              style={{ color: '#F0F4FF' }}
            >
              Built by someone who{' '}
              <span className="gradient-cyan-lavender-text">felt the problem.</span>
            </h2>

            <div className="space-y-5 mb-8">
              <p className="text-base leading-relaxed" style={{ color: 'rgba(240,244,255,0.65)' }}>
                Real estate professionals are drowning in tools. A CRM here. A scheduler there. A separate app for marketing. Another for email. None of them talk to each other — and none of them actually help you decide what to do next.
              </p>
              <p className="text-base leading-relaxed" style={{ color: 'rgba(240,244,255,0.65)' }}>
                The best agents I knew weren't losing deals because of bad software. They were losing deals because they couldn't keep up. Too many clients. Too many follow-ups. Too much noise.
              </p>
              <p className="text-base leading-relaxed" style={{ color: 'rgba(240,244,255,0.65)' }}>
                I built AgentOS because I believed the right AI — one that actually knows your business — could change that. Not another tool to manage. A partner that manages everything for you.
              </p>
              <div
                className="rounded-2xl p-6"
                style={{
                  background: 'rgba(0,212,255,0.06)',
                  border: '1px solid rgba(0,212,255,0.15)',
                }}
              >
                <p className="text-base font-semibold italic leading-relaxed" style={{ color: '#F0F4FF' }}>
                  "The vision is simple: every real estate professional deserves an AI that works as hard as they do — and remembers everything they can't."
                </p>
              </div>
            </div>

            <Link
              href="/in-action"
              className="inline-flex items-center gap-2 text-sm font-semibold transition-colors"
              style={{ color: 'rgba(0,212,255,0.75)' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#00D4FF'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(0,212,255,0.75)'; }}
            >
              See AgentOS in Action
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
